// src/pages/epl/innovatech/components/steps/HipotesisStep.jsx
import React, { useState, useEffect } from 'react';
import { Lightbulb, Sparkles, RefreshCw, Trash2, Check, AlertCircle, Info } from 'lucide-react';
import { generateHypothesisSuggestion } from '../../../../../services/innovatechMockAPI';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
  ? _eplConsole
  : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

/**
 * 🎯 Componente Hipótesis Central - Lab 1: Lean Startup Bootcamp
 * ✨ Rediseñado según EPL UX/UI Policy - Etapa 2 con builder interactivo
 * 
 * @component
 * @description Formulario avanzado para capturar y validar la hipótesis central
 * @param {Function} onSubmit - Callback para procesar la hipótesis
 * @param {boolean} isProcessing - Estado de procesamiento IA
 * @param {Object} labPersistence - Hook de persistencia
 * @param {Object} sessionData - Datos de sesión
 * @param {string} userHypothesis - Hipótesis actual del usuario
 * @param {Object} labData - Datos del laboratorio (título, progreso, etc.)
 */
const HipotesisStep = ({ 
  onSubmit, 
  isProcessing, 
  labPersistence, 
  sessionData, 
  userHypothesis,
  labData 
}) => {
  // 🎯 Estados principales
  const [hypothesis, setHypothesis] = useState('');
  const [builderChips, setBuilderChips] = useState({
    group: '',
    problem: '',
    reason: ''
  });

  const [qualityScores, setQualityScores] = useState({
    clarity: 0,
    focus: 0,
    falsifiability: 0
  });
  const [validationRules, setValidationRules] = useState({
    hasGroup: false,
    observableProblem: false,
    noSolution: false,
    measurableReason: false,
    singleSegment: false
  });
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

  // 📊 Progreso y datos persistidos
  const stepData = labPersistence?.getStepState('hipotesis');
  const approvalData = stepData?.approved ? {
    approved: stepData.approved,
    score: stepData.aiResponse?.score_total || stepData.aiResponse?.score || stepData.score || 0,
    aiResponse: stepData.aiResponse,
    evaluatedAt: stepData.evaluatedAt
  } : null;

  // 💾 Cargar datos iniciales - Solo una vez
  useEffect(() => {
    const initialValue = stepData?.formData || sessionData?.current_hypothesis?.content || userHypothesis || '';
    if (initialValue && hypothesis !== initialValue) {
      setHypothesis(initialValue);
      parseHypothesisToChips(initialValue);
    }
  }, []); // ⚡ Dependencias vacías - solo al montar

  // 🔍 Validaciones en tiempo real - Con dependencias estables
  useEffect(() => {
    validateHypothesis();
    updateQualityScores();
  }, [hypothesis, builderChips.group, builderChips.problem, builderChips.reason]); // ⚡ Dependencias específicas

  // 🎯 Ejemplos por sector (estructura coherente: "tienen [problema]")
  const examples = {
    universidad: {
      group: "estudiantes universitarios de administración de 19-23 años",
      problem: "dificultades para conseguir su primera experiencia laboral formal", 
      reason: "las empresas priorizan candidatos con experiencia previa mínima de 6 meses"
    },
    retail: {
      group: "madres trabajadoras de 28-38 años que compran online",
      problem: "frustración al abandonar compras de productos para niños por falta de información",
      reason: "no encuentran especificaciones de seguridad y calidad en un lugar centralizado"
    },
    salud: {
      group: "adultos mayores de 65 años con múltiples medicamentos",
      problem: "confusión con dosis y horarios de medicamentos recetados",
      reason: "reciben información médica solo en papel y letra pequeña"
    },
    gobierno: {
      group: "emprendedores que inician su primer negocio formal",
      problem: "demoras de más de 15 días en completar trámites de constitución",
      reason: "deben visitar múltiples oficinas sin coordinación entre entidades"
    },
    tecnologia: {
      group: "desarrolladores junior de startups de 22-28 años",
      problem: "pérdida de más de 6 horas semanales en reuniones improductivas",
      reason: "no tienen herramientas que estructuren automáticamente las agendas por prioridad"
    }
  };

  // 🎨 Parser de hipótesis a chips
  const parseHypothesisToChips = (text) => {
    const match = text.match(/creemos que (.+?) tienen (.+?) porque (.+)/i);
    if (match) {
      setBuilderChips({
        group: match[1]?.trim() || '',
        problem: match[2]?.trim() || '',
        reason: match[3]?.trim() || ''
      });
    }
  };

  // � Validaciones de reglas
  const validateHypothesis = () => {
    const text = hypothesis.toLowerCase();
    setValidationRules({
      hasGroup: builderChips.group.length > 5 || /estudiantes|usuarios|clientes|pacientes|ciudadanos/.test(text),
      observableProblem: builderChips.problem.length > 10 || /dificultad|problema|necesidad|frustración/.test(text),
      noSolution: !/solución|app|plataforma|sistema|herramienta/.test(text),
      measurableReason: builderChips.reason.length > 10 || /porque|debido|ya que/.test(text),
      singleSegment: !text.includes(' y ') && !text.includes(' o ')
    });
  };

  // 📊 Puntajes de calidad
  const updateQualityScores = () => {
    const charCount = hypothesis.length;
    const wordsCount = hypothesis.split(' ').length;
    
    setQualityScores({
      clarity: Math.min(100, Math.max(0, (charCount / 120) * 60 + (wordsCount > 5 ? 40 : 0))),
      focus: builderChips.group && builderChips.problem ? 85 : 0,
      falsifiability: builderChips.reason && hypothesis.includes('porque') ? 90 : 0
    });
  };

  // 🧩 Manejo de chips
  const updateChip = (type, value) => {
    const updated = { ...builderChips, [type]: value };
    setBuilderChips(updated);
    
    // Auto-generar hipótesis desde chips
    if (updated.group && updated.problem && updated.reason) {
      const generated = `Creemos que ${updated.group} tienen ${updated.problem} porque ${updated.reason}.`;
      setHypothesis(generated);
    }
  };

  // 🤖 IA auto-completar
  const fillWithAI = async () => {
    setIsGeneratingSuggestion(true);
    
    try {
      // 🎯 Rotar sector aleatoriamente para variedad
      const availableSectors = ['universidad', 'retail', 'salud', 'gobierno', 'tecnologia'];
      const randomSector = availableSectors[Math.floor(Math.random() * availableSectors.length)];
      
      console.log('🎲 Sector seleccionado para IA:', randomSector);
      
      // 📝 Contexto más específico para la IA
      const baseContext = labData?.description || 'problema de usuario no especificado';
      const contextWithSector = `${baseContext}. Enfócate en el sector ${randomSector}. Genera una hipótesis específica y diferente a ejemplos anteriores.`;
      
      // Llamar a la IA para generar sugerencia
      const result = await generateHypothesisSuggestion(contextWithSector, randomSector);
      
      console.log('🤖 Respuesta IA completa:', result);
      
      if (result.success && result.suggestion) {
        const { group, problem, reason } = result.suggestion;
        setBuilderChips({ group, problem, reason });
        
        const generated = `Creemos que ${group} tienen ${problem} porque ${reason}.`;
        setHypothesis(generated);
        
        console.log(`✅ Sugerencia generada con ${result.source} para sector ${randomSector}:`, result.suggestion);
      } else {
        throw new Error('No se pudo generar sugerencia');
      }
      
    } catch (error) {
      console.warn('⚠️ Error generando sugerencia, usando fallback variado:', error);
      
      // 🎲 Fallback con sector aleatorio también
      const availableSectors = ['universidad', 'retail', 'salud', 'gobierno'];
      const randomFallbackSector = availableSectors[Math.floor(Math.random() * availableSectors.length)];
      
      const aiSuggestion = examples[randomFallbackSector];
      setBuilderChips(aiSuggestion);
      
      const generated = `Creemos que ${aiSuggestion.group} tienen ${aiSuggestion.problem} porque ${aiSuggestion.reason}.`;
      setHypothesis(generated);
      
      console.log('📋 Usando fallback para sector:', randomFallbackSector);
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  // 🗑️ Limpiar
  const clearAll = () => {
    // Limpiar todos los estados
    setBuilderChips({ group: '', problem: '', reason: '' });
    setHypothesis('');
    
    // Forzar limpieza de los inputs manualmente por si acaso
    const groupInput = document.getElementById('group-input');
    const problemInput = document.getElementById('problem-input');
    const reasonInput = document.getElementById('reason-input');
    
    if (groupInput) groupInput.value = '';
    if (problemInput) problemInput.value = '';
    if (reasonInput) reasonInput.value = '';
    
    console.log('🗑️ Campos limpiados completamente');
  };



  // ✅ Verificar si puede validar con IA
  const canValidate = () => {
    return hypothesis.length >= 120 && 
           builderChips.group && 
           builderChips.problem && 
           builderChips.reason && 
           qualityScores.clarity >= 60;
  };

  // 📊 Calcular progreso de reglas
  const rulesProgress = Object.values(validationRules).filter(Boolean).length / Object.keys(validationRules).length * 100;

  return (
    <div className="space-y-6 mt-4 md:mt-6">
      {/* 🎯 Card principal de hipótesis */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 md:p-7">
        {/* 🎨 Header de la tarjeta */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-teal-700" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Describe tu hipótesis central
            </h2>
            <p className="text-sm text-gray-600">
              Completa los 3 bloques para que tu hipótesis sea comprobable
            </p>
          </div>
        </div>

        {/* 🧩 1. Builder interactivo con chips */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-sm font-medium text-gray-700">
              1. Constructor de hipótesis
            </h3>
            <div className="relative group">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Ej: "estudiantes de ingeniería tienen dificultades laborales porque empresas piden experiencia"
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          </div>
          
          <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-700 mb-3">
              <span className="font-medium">Estructura:</span> Creemos que{" "}
              
              {/* Chip: Grupo de clientes */}
              <button
                onClick={() => document.getElementById('group-input')?.focus()}
                className={`inline-flex items-center px-2 py-1 mx-1 rounded text-sm border transition-colors ${
                  builderChips.group 
                    ? 'bg-teal-100 border-teal-200 text-teal-800' 
                    : 'bg-white border-teal-300 text-gray-600 hover:border-teal-400'
                }`}
              >
                {builderChips.group || 'grupo de clientes'}
              </button>
              
              {" tienen "}
              
              {/* Chip: Problema */}
              <button
                onClick={() => document.getElementById('problem-input')?.focus()}
                className={`inline-flex items-center px-2 py-1 mx-1 rounded text-sm border transition-colors ${
                  builderChips.problem 
                    ? 'bg-teal-100 border-teal-200 text-teal-800' 
                    : 'bg-white border-teal-300 text-gray-600 hover:border-teal-400'
                }`}
              >
                {builderChips.problem || 'problema'}
              </button>
              
              {" porque "}
              
              {/* Chip: Razón observable */}
              <button
                onClick={() => document.getElementById('reason-input')?.focus()}
                className={`inline-flex items-center px-2 py-1 mx-1 rounded text-sm border transition-colors ${
                  builderChips.reason 
                    ? 'bg-teal-100 border-teal-200 text-teal-800' 
                    : 'bg-white border-teal-300 text-gray-600 hover:border-teal-400'
                }`}
              >
                {builderChips.reason || 'razón observable'}
              </button>
            </div>

            {/* Inputs expandidos con etiquetas contextuales */}
            <div className="space-y-4">
              {/* 1. Cliente */}
              <div className="space-y-1.5">
                <label htmlFor="group-input" className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                  1. Cliente
                </label>
                <input
                  id="group-input"
                  type="text"
                  value={builderChips.group}
                  onChange={(e) => updateChip('group', e.target.value)}
                  placeholder="estudiantes universitarios de ingeniería..."
                  className="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              
              {/* 2. Problema */}
              <div className="space-y-1.5">
                <label htmlFor="problem-input" className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                  2. Problema
                </label>
                <input
                  id="problem-input"
                  type="text"
                  value={builderChips.problem}
                  onChange={(e) => updateChip('problem', e.target.value)}
                  placeholder="dificultades para..."
                  className="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              
              {/* 3. Motivo */}
              <div className="space-y-1.5">
                <label htmlFor="reason-input" className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                  3. Motivo
                </label>
                <input
                  id="reason-input"
                  type="text"
                  value={builderChips.reason}
                  onChange={(e) => updateChip('reason', e.target.value)}
                  placeholder="porque... (evidencia/comportamiento medible)"
                  className="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Botones auxiliares */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={fillWithAI}
                disabled={isGeneratingSuggestion}
                className={`flex items-center gap-1 px-3 py-1.5 text-white text-sm rounded transition-colors ${
                  isGeneratingSuggestion 
                    ? 'bg-teal-400 cursor-not-allowed' 
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {isGeneratingSuggestion ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {isGeneratingSuggestion ? 'Generando...' : 'Rellenar con sugerencia IA'}
              </button>
              <button
                onClick={clearAll}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 text-sm rounded hover:bg-gray-50 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                Limpiar
              </button>
            </div>
          </div>
        </div>



        {/* ✏️ 2. Área de redacción libre */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            2. Hipótesis completa
          </h3>
          
          <div className="relative">
            <textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="Escribe aquí tu hipótesis completa usando la estructura. Mín. 120 caracteres."
              rows={4}
              className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
            />
            
            {/* Contador de caracteres */}
            <div className="absolute bottom-3 right-3 text-xs">
              <span className={hypothesis.length >= 120 ? 'text-green-600' : 'text-gray-500'}>
                {hypothesis.length}/120
              </span>
            </div>
          </div>

          {/* Ayudas en línea - píldoras con estado */}
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-600 mb-2">Marcamos ✅ cuando tu texto cumpla cada criterio:</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'hasGroup', label: 'Incluye grupo específico' },
                { key: 'observableProblem', label: 'Problema observable (no opinión)' },
                { key: 'noSolution', label: 'Evita soluciones en la hipótesis' },
                { key: 'measurableReason', label: 'Contiene una razón medible' },
                { key: 'singleSegment', label: 'Mantiene foco en un solo segmento' }
              ].map(({ key, label }) => (
                <div
                  key={key}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                    validationRules[key]
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {validationRules[key] ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <AlertCircle className="w-3 h-3" />
                  )}
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* � 3. CTA Validar con IA */}
        <div className="flex justify-center">
          <button
            onClick={() => onSubmit && onSubmit(hypothesis)}
            disabled={!canValidate() || isProcessing}
            className={`
              w-full sm:w-auto px-4 py-3 rounded-lg font-semibold
              flex items-center justify-center gap-2
              transition-all duration-200 ease-in-out
              ${canValidate() && !isProcessing
                ? 'bg-teal-700 hover:bg-teal-800 text-white transform hover:scale-101'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
              ${isProcessing ? 'animate-pulse' : ''}
            `}
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Evaluando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Validar con IA
              </>
            )}
          </button>
        </div>

        {/* 🔍 DIAGNÓSTICO TEMPORAL - ELIMINADO - Sistema funcionando */}
        {false && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 mb-2">🔧 Diagnóstico de Conectividad:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    const isProduction = window.location.hostname.includes('edutechpulselab.com');
                    const API_BASE_URL = import.meta.env.VITE_API_URL || 
                      (isProduction 
                        ? `https://edutechpulselab.com/api`
                        : 'http://localhost:3001/api');
                    
                    console.log('🔍 Testing connectivity to:', API_BASE_URL);
                    
                    const response = await fetch(`${API_BASE_URL}/health`, {
                      method: 'GET',
                      headers: { 'Content-Type': 'application/json' }
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      alert(`✅ Backend OK - ${response.status}\nMongoDB: ${data.mongodb}\nOpenAI: ${data.openai}\n\nURL usada: ${API_BASE_URL}`);
                    } else {
                      alert(`❌ Backend Error: ${response.status}\nURL: ${API_BASE_URL}\n\nVerificar:\n1. Backend corriendo (PM2)\n2. Proxy configurado\n3. Permisos de firewall`);
                    }
                  } catch (err) {
                    alert(`❌ Conexión Error: ${err.message}\n\nURL intentada: ${API_BASE_URL || 'undefined'}\n\nProbable causa:\n- Proxy no configurado\n- Backend no responde\n- Certificados SSL`);
                    console.error('Connectivity test failed:', err);
                  }
                }}
                className="px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded"
              >
                🧪 Test Backend
              </button>
              <div className="text-xs text-gray-600 space-y-1">
                <div>URL: {import.meta.env.VITE_API_URL || (window.location.hostname.includes('edutechpulselab.com') ? 'https://edutechpulselab.com/api' : 'http://localhost:3001/api')}</div>
                <div>Entorno: {window.location.hostname.includes('edutechpulselab.com') ? 'PRODUCCIÓN (con proxy)' : 'DESARROLLO'}</div>
                <div>Mobile: {/Mobi|Android/i.test(navigator.userAgent) ? 'Sí' : 'No'}</div>
              </div>
            </div>
          </div>
        )}

        {/* 🎓 Feedback del Tutor IA - Según Especificaciones del Experto */}
        {approvalData && (
          <div className="mt-8 mx-auto max-w-2xl">
            {/* 1. Encabezado compacto */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-lg">🎓</span>
                <div>
                  <h4 className="font-semibold text-sm">Feedback del Tutor IA</h4>
                  <p className="text-xs text-gray-600">Análisis pedagógico de tu hipótesis</p>
                </div>
              </div>
              <div className="px-2 py-1 bg-blue-100 text-blue-800 text-[11px] font-medium rounded">
                OpenAI
              </div>
            </div>

            {/* 2. Banner de estado */}
            <div className={`mb-6 p-4 rounded-lg ${
              approvalData.approved || approvalData.score >= 70
                ? 'bg-green-50 border border-green-200'
                : approvalData.score >= 60
                ? 'bg-amber-50 border border-amber-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className={`font-medium ${
                    approvalData.approved || approvalData.score >= 70 ? 'text-green-800' : approvalData.score >= 60 ? 'text-amber-800' : 'text-red-800'
                  }`}>
                    {approvalData.approved || approvalData.score >= 70 
                      ? '¡Hipótesis Aprobada!' 
                      : approvalData.score >= 60 
                      ? 'En mejora' 
                      : 'Requiere revisión'}
                  </h4>
                </div>
                <div className={`text-xl font-bold ${
                  approvalData.approved || approvalData.score >= 70 ? 'text-green-700' : approvalData.score >= 60 ? 'text-amber-700' : 'text-red-700'
                }`}>
                  {approvalData.score}/100
                </div>
              </div>
            </div>

            {/* 3. Métricas principales con colores por umbral */}
            {approvalData.aiResponse?.scores && (
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { key: 'claridad', label: 'Claridad', value: approvalData.aiResponse.scores.claridad || 0 },
                  { key: 'foco_cliente', label: 'Foco Cliente', value: approvalData.aiResponse.scores.foco_cliente || 0 },
                  { key: 'testabilidad', label: 'Falsabilidad', value: approvalData.aiResponse.scores.testabilidad || approvalData.aiResponse.scores.viabilidad || 0 }
                ].map(({ key, label, value }) => {
                  const getColorClasses = (score) => {
                    if (score >= 80) return { bg: 'bg-emerald-500', text: 'text-emerald-600' };
                    if (score >= 70) return { bg: 'bg-blue-500', text: 'text-blue-600' };
                    return { bg: 'bg-amber-400', text: 'text-amber-600' };
                  };
                  
                  const colors = getColorClasses(value);
                  
                  return (
                    <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                        <span className={`font-semibold ${colors.text}`}>{value}/100</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 ${colors.bg} rounded-full transition-all duration-500`}
                          style={{ width: `${value}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 4. Análisis detallado según especificaciones exactas del experto */}
            {approvalData.aiResponse && (
              <div className="space-y-3">
                {/* Fortaleza */}
                {approvalData.aiResponse.fortaleza && (
                  <article className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                    <h4 className="text-[11px] font-semibold uppercase text-emerald-700 mb-1 tracking-wide">
                      1) Fortaleza
                    </h4>
                    <p className="text-sm text-emerald-900">
                      {approvalData.aiResponse.fortaleza}
                    </p>
                  </article>
                )}

                {/* Mejora */}
                {approvalData.aiResponse.mejora && (
                  <article className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                    <h4 className="text-[11px] font-semibold uppercase text-amber-700 mb-1 tracking-wide">
                      2) Mejora
                    </h4>
                    <p className="text-sm text-amber-900">
                      {approvalData.aiResponse.mejora}
                    </p>
                  </article>
                )}

                {/* Acción Sugerida */}
                {approvalData.aiResponse.accion && (
                  <article className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                    <h4 className="text-[11px] font-semibold uppercase text-blue-700 mb-1 tracking-wide">
                      3) Acción Sugerida
                    </h4>
                    <p className="text-sm text-blue-900">
                      {approvalData.aiResponse.accion}
                    </p>
                  </article>
                )}
              </div>
            )}

            {/* 5. Conclusión pedagógica */}
            {approvalData.aiResponse?.conclusion && (
              <div className="rounded-md bg-gray-50 border border-gray-100 p-3 mt-3">
                <p className="text-xs text-gray-700">
                  <strong>Conclusión:</strong> {approvalData.aiResponse.conclusion}
                </p>
              </div>
            )}

            {/* 6. Footer auxiliar */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-[11px] text-gray-500">
                Rol: Tutor • Contrato: v1.1 • Generado: {new Date().toLocaleString('es-ES')}
              </p>
            </div>

            {/* Mensaje para avance cuando score ≥ 70 */}
            {(approvalData.score >= 70) && (
              <div className="mt-4 text-center">
                <p className="text-xs text-green-600 font-medium">
                  Tu hipótesis ya puede pasar a MVP.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 📱 Barra de progreso móvil */}
      <div className="sm:hidden fixed top-0 left-0 right-0 z-40">
        <div className="h-0.5 bg-gray-200">
          <div 
            className="h-full bg-teal-600 transition-all duration-300" 
            style={{ width: `${labData?.progress || 25}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default HipotesisStep;